import type { TierId } from '#shared/types/tiers'
import { TIER_DEPOSITS } from '#shared/constants/foundation'
import { SNAPSHOT_BOOST_MULTIPLIER, TIER_WEIGHTS } from '#shared/constants/vaultRules'

/**
 * The payout rule, the one place it is written down.
 *
 * Every period the vault's collected fees are split across eligible members by weight:
 *
 *   payout = pot × weight / Σ weight of eligible members
 *   pot    = claimable vault fees (the fee's share of the Aave yield on everything in the vault)
 *   weight = the weight of the tier the member is paid at (`payoutTier`), × the check-in
 *            boost if the member checked in this period
 *
 * Distributions call `splitPot`. Every figure shown to a visitor or member calls
 * `projectPayout`, which is the same rule over a year. Projections leave the check-in boost
 * out: when every member checks in it changes nobody's share, so it is not a number anyone
 * can count on.
 *
 * Nothing here falls back to a made-up rate. If an input is missing or unusable the inputs
 * are `null` and the page shows no figure.
 */

/** Distributions land on the 1st and the 15th. */
export const DISTRIBUTIONS_PER_YEAR = 24

/** Live inputs, all read from the vault and the membership aggregates. */
export interface PayoutInputs {
  /** Aave's USDC supply APY, in percent. */
  aaveApyPercent: number
  /** Share of the vault's yield collected as fees and paid out, 0 to 1. */
  feeRate: number
  /** Everything in the vault, in USDC. */
  totalAssets: number
  /** Members eligible for the next distribution, by tier. Trial members take weight too. */
  membersByTier: Partial<Record<TierId, number>>
}

/**
 * Validate raw metrics into projection inputs. Returns `null` unless every input is loaded
 * and usable, so a partial load never produces a figure.
 */
export function toPayoutInputs(raw: {
  aaveApyPercent: number | null
  feeRate: number | null
  totalAssets: number | null
  membersByTier: Partial<Record<TierId, number>> | null
}): PayoutInputs | null {
  const { aaveApyPercent, feeRate, totalAssets, membersByTier } = raw

  if (aaveApyPercent == null || !Number.isFinite(aaveApyPercent) || aaveApyPercent <= 0)
    return null
  if (feeRate == null || !Number.isFinite(feeRate) || feeRate <= 0 || feeRate > 1)
    return null
  if (totalAssets == null || !Number.isFinite(totalAssets) || totalAssets < 0)
    return null
  if (membersByTier == null)
    return null

  return { aaveApyPercent, feeRate, totalAssets, membersByTier }
}

/** A member's share of the split. The only reader of the tier weights and the boost. */
export function memberWeight(tier: TierId, { boosted = false }: { boosted?: boolean } = {}): number {
  const weight = TIER_WEIGHTS[tier]
  return boosted ? weight * SNAPSHOT_BOOST_MULTIPLIER : weight
}

/** The membership columns that hold back a recent upgrade. */
export interface TierUpgradeRecord {
  selected_tier: string | null
  /** The tier held before an upgrade that has not yet earned its weight. */
  previous_tier: string | null
  /** When the upgraded tier starts to pay. Null, or passed, once no upgrade is waiting. */
  tier_eligible_date: string | null
}

// A plain list rather than the zod enum, so the landing page's payout maths needs no zod.
const TIER_IDS: readonly TierId[] = ['trial', 'bronze', 'silver', 'gold']

function parseTier(value: string | null): TierId | null {
  if (value == null)
    return null
  const tier = value.toLowerCase()
  return TIER_IDS.find(id => id === tier) ?? null
}

/**
 * The tier a member is paid at. An upgrade earns its weight only once the new tier has been
 * held for a full distribution period, the same wait a new member sits out, so a top-up on the
 * morning of a payout cannot buy a bigger share of it. Until `tier_eligible_date` passes the
 * member is paid at the lower of the tier they came from and the one they hold now. `null` for a
 * record with no recognised tier.
 */
export function payoutTier(record: TierUpgradeRecord, now: Date): TierId | null {
  const selected = parseTier(record.selected_tier)
  if (selected == null)
    return null

  const previous = parseTier(record.previous_tier)
  const eligibleFrom = Date.parse(record.tier_eligible_date ?? '')
  if (previous == null || Number.isNaN(eligibleFrom) || eligibleFrom <= now.getTime())
    return selected

  return memberWeight(previous) < memberWeight(selected) ? previous : selected
}

/** Sum of weights across counted members, unboosted. */
export function totalWeight(membersByTier: Partial<Record<TierId, number>>): number {
  return TIER_IDS
    .reduce((sum, tier) => sum + (membersByTier[tier] ?? 0) * memberWeight(tier), 0)
}

export interface PotMember {
  /** `null` for a member with no recognised tier; they take no share. */
  tier: TierId | null
  boosted?: boolean
}

/** Split a pot across members by weight. Returns each member's amount, in order. */
export function splitPot(pot: number, members: readonly PotMember[]): number[] {
  const weights = members.map(m => m.tier == null ? 0 : memberWeight(m.tier, { boosted: m.boosted }))
  const total = weights.reduce((sum, w) => sum + w, 0)

  return weights.map(w => total > 0 ? (w / total) * pot : 0)
}

/** A year of fees at today's rate: the fee's share of the Aave yield on the whole vault. */
export function yearlyPot({ aaveApyPercent, feeRate, totalAssets }: Pick<PayoutInputs, 'aaveApyPercent' | 'feeRate' | 'totalAssets'>): number {
  return (aaveApyPercent / 100) * feeRate * totalAssets
}

/**
 * Who the projection is for.
 *
 * - `member`: already counted in `membersByTier`, with `deposit` already in the vault.
 * - `newcomer`: joins `tier`. `deposit` is what they already hold in the vault, if anything;
 *   they top it up to the tier minimum.
 * - `tierChange`: a counted member moving from `from` to `to`, topping `deposit` up to the new
 *   minimum if it falls short. A downgrade keeps the deposit where it is.
 */
export type PayoutScenario
  = | { kind: 'member', tier: TierId, deposit: number }
    | { kind: 'newcomer', tier: TierId, deposit?: number }
    | { kind: 'tierChange', from: TierId, to: TierId, deposit: number }

export interface PayoutProjection {
  perYear: number
  perPeriod: number
  /** `perYear` as a percentage of the deposit; `null` with no deposit, as for trial. */
  apy: number | null
}

export function projectPayout(inputs: PayoutInputs, scenario: PayoutScenario): PayoutProjection {
  const { tier, deposit, addedWeight } = normalizeScenario(scenario)
  const weight = memberWeight(tier)
  const addedAssets = Math.max(0, deposit - (scenario.deposit ?? 0))

  const pot = yearlyPot({ ...inputs, totalAssets: inputs.totalAssets + addedAssets })
  // A counted member is part of the total. Flooring at their own weight keeps a member the
  // counts missed from being paid more than the whole pot.
  const weightAfter = Math.max(totalWeight(inputs.membersByTier) + addedWeight, weight)
  const perYear = weightAfter > 0 ? pot * weight / weightAfter : 0

  return {
    perYear,
    perPeriod: perYear / DISTRIBUTIONS_PER_YEAR,
    apy: deposit > 0 ? perYear / deposit * 100 : null,
  }
}

export interface PayoutGain {
  /** What the target scenario deposits on top of the baseline, in USDC. */
  extraDeposit: number
  /** What the target scenario pays on top of the baseline, a year. */
  extraPerYear: number
}

/**
 * What choosing `target` over `baseline` adds for the same payee: the extra deposit and the
 * extra payout a year. A counted member weighing an upgrade compares `tierChange` against
 * `member` at their current tier; an applicant compares one `newcomer` tier against another.
 */
export function projectPayoutGain(inputs: PayoutInputs, baseline: PayoutScenario, target: PayoutScenario): PayoutGain {
  return {
    extraDeposit: normalizeScenario(target).deposit - normalizeScenario(baseline).deposit,
    extraPerYear: projectPayout(inputs, target).perYear - projectPayout(inputs, baseline).perYear,
  }
}

/** The payee's tier, their deposit once the scenario has happened, and the weight it adds. */
function normalizeScenario(scenario: PayoutScenario): { tier: TierId, deposit: number, addedWeight: number } {
  switch (scenario.kind) {
    case 'member':
      return { tier: scenario.tier, deposit: scenario.deposit, addedWeight: 0 }
    case 'newcomer':
      return {
        tier: scenario.tier,
        deposit: Math.max(scenario.deposit ?? 0, TIER_DEPOSITS[scenario.tier]),
        addedWeight: memberWeight(scenario.tier),
      }
    case 'tierChange':
      return {
        tier: scenario.to,
        deposit: Math.max(scenario.deposit, TIER_DEPOSITS[scenario.to]),
        addedWeight: memberWeight(scenario.to) - memberWeight(scenario.from),
      }
  }
}
